/**
 * Frontend service for the new bookkeeping-templates API.
 *
 * Coexists with the legacy template endpoints during the cutover —
 * the new wizard / page consumes THIS module, the legacy
 * BookkeepingEntryWizard still uses the old /:companyId/templates
 * routes via inline axios calls.
 *
 * Backend routes (see accounting-backend/bookkeepingTemplateRoutes.js):
 *   POST   /:companyId/bookkeeping-templates
 *   GET    /:companyId/bookkeeping-templates
 *   GET    /:companyId/bookkeeping-templates/:id
 *   PUT    /:companyId/bookkeeping-templates/:id
 *   POST   /:companyId/bookkeeping-templates/:id/submit-for-sharing
 *   POST   /:companyId/bookkeeping-templates/:id/suggest
 *   GET    /admin/bookkeeping-templates/pending
 *   PUT    /admin/bookkeeping-templates/:id/approve
 *   PUT    /admin/bookkeeping-templates/:id/reject
 */

import axiosInstance from './AxiosInstance';
import { getApiUrl } from '../utils/ApiUtils';

function tenantBase(companyId) {
    return `${companyId}/bookkeeping-templates`;
}

/**
 * GET /:companyId/bookkeeping-templates
 * Returns { success, templates: [...] } where each template has the
 * full row shape including parsed `body` and `source_citations`
 * (already JSON-deserialized by the backend repos). Filtered by
 * applicable_company_types matching the tenant's company_type, plus
 * the tenant's own pending/rejected submissions regardless of type.
 */
export async function listBookkeepingTemplates(companyId) {
    const url = getApiUrl(tenantBase(companyId));
    const response = await axiosInstance.get(url);
    return response.data.templates || [];
}

/**
 * GET /:companyId/bookkeeping-templates/:id
 */
export async function getBookkeepingTemplate(companyId, templateId) {
    const url = getApiUrl(`${tenantBase(companyId)}/${templateId}`);
    const response = await axiosInstance.get(url);
    return response.data.template;
}

/**
 * POST /:companyId/bookkeeping-templates/:id/suggest
 * Body: { inputs: { name: value, ... } }
 * Returns: { success, ok, lines: [...], totals: {debit,credit}, errors? }
 * Status 200 on ok=true, 422 on validation/balance failure.
 *
 * Lines are enriched with `account_name` from the tenant chart (see
 * BookkeepingTemplateService.suggestVerification on the backend).
 *
 * We swallow 422 here and return the body unchanged — the caller
 * surfaces the errors to the user. Other status codes throw.
 */
export async function suggestVerification(companyId, templateId, inputs, context = null) {
    const url = getApiUrl(`${tenantBase(companyId)}/${templateId}/suggest`);
    try {
        const response = await axiosInstance.post(url, { inputs, ...(context ? { context } : {}) });
        return response.data;
    } catch (err) {
        if (err.response && err.response.status === 422) {
            if (err.response.data?.code === 'template_incompatible') throw err;
            return err.response.data;
        }
        throw err;
    }
}

/**
 * POST /:companyId/bookkeeping-templates/preview
 * Body: { body: <template body JSON>, inputs: { name: value, ... } }
 * Returns: { success, ok, lines: [...], totals: {debit,credit}, errors? }
 *
 * Stateless preview — evaluates an UNSAVED template body so the editor
 * can show a live verifikat while the author is still typing. Same
 * result shape and 200/422 convention as suggestVerification(), and
 * the same evaluator runs server-side — so the editor preview is
 * identical to what the verifikat wizard shows once the template is
 * saved. 422 (well-formed body, invalid verifikat) is swallowed and
 * the body returned so the caller surfaces the evaluator errors.
 */
export async function previewVerification(companyId, body, inputs, contract = {}) {
    const url = getApiUrl(`${tenantBase(companyId)}/preview`);
    try {
        const response = await axiosInstance.post(url, {
            body,
            inputs,
            applicable_company_types: contract.applicableCompanyTypes,
            supported_chart_versions: contract.supportedChartVersions,
            supported_accounting_regimes: contract.supportedAccountingRegimes,
        });
        return response.data;
    } catch (err) {
        if (err.response && err.response.status === 422) {
            if (err.response.data?.code === 'template_incompatible') throw err;
            return err.response.data;
        }
        throw err;
    }
}

/**
 * POST /:companyId/bookkeeping-templates/recipe
 * Guided-mode recipe builder. Sends the plain choices the guided form
 * collected (recipe_type, vat, accounts…); the backend applies all the
 * accounting logic and returns a complete, non-persisted template
 * definition: { template_name, description, template_type,
 * transaction_origin, category, body }.
 *
 * 4xx (invalid choices) is thrown — the caller maps it via
 * toUserErrorMessage and surfaces it in the guided form.
 */
export async function buildRecipe(companyId, choices) {
    const url = getApiUrl(`${tenantBase(companyId)}/recipe`);
    const response = await axiosInstance.post(url, choices);
    return response.data.template;
}

/**
 * POST /:companyId/bookkeeping-templates
 * Creates a new template. visibility: 'private' (default) | 'shared'.
 */
export async function createBookkeepingTemplate(companyId, payload) {
    const url = getApiUrl(tenantBase(companyId));
    const response = await axiosInstance.post(url, payload);
    return response.data;
}

/**
 * PUT /:companyId/bookkeeping-templates/:id
 * Note: approved-shared templates are immutable — backend returns
 * 409 with code 'shared_template_immutable'. The caller should
 * inspect err.response.data.code for branching UX.
 */
export async function updateBookkeepingTemplate(companyId, templateId, payload) {
    const url = getApiUrl(`${tenantBase(companyId)}/${templateId}`);
    const response = await axiosInstance.put(url, payload);
    return response.data;
}

/**
 * DELETE /:companyId/bookkeeping-templates/:id
 * Soft-deletes (deactivates) a tenant-owned template. System/shared
 * catalog templates are not the tenant's to delete — the backend
 * returns 404 for those.
 */
export async function deleteBookkeepingTemplate(companyId, templateId) {
    const url = getApiUrl(`${tenantBase(companyId)}/${templateId}`);
    const response = await axiosInstance.delete(url);
    return response.data;
}

/**
 * POST /:companyId/bookkeeping-templates/:id/usage
 *
 * Fire-and-forget usage counter bump. Called after a successful verifikat
 * post so the picker can surface "Senast använda" (DEFERRED 48). Failure
 * is non-fatal — callers should swallow rejection so a flaky network
 * doesn't surface a meaningless toast to the user. Returns the success
 * boolean for callers that want to log or react.
 */
export async function recordTemplateUsage(companyId, templateId) {
    if (!companyId || !templateId) return false;
    try {
        const url = getApiUrl(`${tenantBase(companyId)}/${templateId}/usage`);
        await axiosInstance.post(url);
        return true;
    } catch {
        return false;
    }
}

/**
 * POST /:companyId/bookkeeping-templates/:id/submit-for-sharing
 */
export async function submitTemplateForSharing(companyId, templateId) {
    const url = getApiUrl(`${tenantBase(companyId)}/${templateId}/submit-for-sharing`);
    const response = await axiosInstance.post(url);
    return response.data;
}

// ---- Admin endpoints ----

export async function listPendingTemplates() {
    const url = getApiUrl('admin/bookkeeping-templates/pending');
    const response = await axiosInstance.get(url);
    return response.data.templates || [];
}

export async function approveTemplate(templateId) {
    const url = getApiUrl(`admin/bookkeeping-templates/${templateId}/approve`);
    const response = await axiosInstance.put(url);
    return response.data;
}

export async function rejectTemplate(templateId, rejectionReason) {
    const url = getApiUrl(`admin/bookkeeping-templates/${templateId}/reject`);
    const response = await axiosInstance.put(url, { rejection_reason: rejectionReason });
    return response.data;
}

/**
 * Map an axios-style error from any of the calls above to a
 * user-friendly Swedish string. Used by the wizard's error banners
 * so users don't see "Request failed with status code 403" verbatim.
 *
 * Special handling:
 *   - 401: handled by AxiosInstance interceptor before reaching here
 *          (token refresh + auto-logout). If it surfaces, the user
 *          is already being redirected.
 *   - 403: tenant lacks access OR not platform-admin for admin paths
 *   - 404: template-not-found is a real user state, not an error
 *   - 409: immutability / state-conflict (approved-shared templates,
 *          duplicate submissions, race-lost on approval transition)
 *   - 4xx with a `code` in the response body: server already provided
 *          a structured error; surface its `message` field unchanged
 *          (the backend writes Swedish copy on those paths)
 *   - 5xx: opaque server error
 */
export function toUserErrorMessage(err) {
    if (!err) return 'Ett okänt fel uppstod.';
    if (!err.response) {
        // No HTTP response — network down, CORS, etc.
        return 'Det går inte att nå servern just nu. Försök igen om en stund.';
    }
    const { status, data } = err.response;
    if (data && typeof data.message === 'string' && data.message.length > 0) {
        // Backend ServiceError already speaks Swedish.
        return data.message;
    }
    if (status === 401) return 'Du måste logga in igen.';
    if (status === 403) return 'Du saknar behörighet för den här åtgärden.';
    if (status === 404) return 'Hittade inte det du letade efter.';
    if (status === 409) return 'Åtgärden krockar med befintliga data — försök ladda om sidan.';
    if (status === 422) return 'Uppgifterna är inte kompletta — kontrollera fälten.';
    if (status >= 500) return 'Något gick fel hos oss. Försök igen om en stund.';
    return `Något gick fel (kod ${status}).`;
}

export function incompatibilityDetails(err) {
    const data = err?.response?.data;
    if (data?.code !== 'template_incompatible' || !Array.isArray(data.details)) return [];
    return data.details.map((detail) => ({
        path: detail.jsonPath || detail.path || '',
        reference: detail.account || detail.role || '',
        chartVersion: detail.chartVersion || '',
        reason: detail.reason || '',
        reasonText: incompatibilityReasonText(detail.reason),
    }));
}

const INCOMPATIBILITY_REASON_SV = Object.freeze({
    account_semantics_displaced: 'Kontonumret har en annan eller egen betydelse i företaget.',
    role_default_not_applicable_to_company_type: 'Standardkontot gäller inte för företagsformen.',
    account_absent_from_tenant_chart: 'Kontot finns inte i företagets kontoplan.',
    account_archived: 'Kontot är arkiverat.',
    chart_not_declared: 'Mallen är inte godkänd för företagets kontoplan.',
    regime_not_declared: 'Mallen är inte godkänd för företagets redovisningsregelverk.',
    company_type_not_declared: 'Mallen är inte godkänd för företagsformen.',
    ROLE_UNSUPPORTED_FOR_CHART: 'Den bokföringsrollen stöds inte av företagets kontoplan.',
    DEFAULT_ACCOUNT_ARCHIVED: 'Rollens standardkonto är arkiverat.',
    DEFAULT_ACCOUNT_NEEDS_MAPPING: 'Rollens standardkonto har en annan betydelse och måste väljas uttryckligen.',
});

export function incompatibilityReasonText(reason) {
    if (!reason) return '';
    return INCOMPATIBILITY_REASON_SV[reason]
        || 'Mallen är inte kompatibel med företagets aktuella bokföringsinställningar.';
}
